import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { AsyncPipe, DOCUMENT, Location, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { LOCAL_STORAGE, NAVIGATOR, WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { NEVER, Observable, filter, from, fromEvent, map, mergeMap, of, tap, switchMap, finalize } from 'rxjs';
import { CookiePreference, UserRole } from 'src/types/graphql';
import { v4 as uuidv4 } from 'uuid';
import { routes } from '../../utils/routes';
import { environment } from '../environments/environment';
import { AnalyticsService } from './common/analytics/analytics.service';
import { GoogleAnalyticsService } from './common/analytics/google-analytics.service';
import { BaseComponent } from './_shared/components/base/base.component';
import { CAPACITOR_SERVICE, CapacitorService } from './common/capacitor/capacitor.service';
import { CookieButtonComponent } from './_shared/components/cookie-button/cookie-button.component';
import { CookieService } from './common/cookie/cookie.service';
import { CookiePreferenceCreateGQL } from './_shared/dialogs/cookie-dialog/cookie-dialog.mutation.generated';
import { CookiePreferenceGQL } from './_shared/dialogs/cookie-dialog/cookie-dialog.query.generated';
import { SignInService } from './_shared/dialogs/sign-in/sign-in.service';
import { LocationSearchService } from './common/location-search/location-search.service';
import { METADATA_SERVICE, MetadataService } from './common/metadata/metadata.service';
import { NavigationService } from './common/navigation/navigation.service';
import { OpenDialogService } from './common/open-dialog/open-dialog.service';
import { OpenSnackBarService } from './common/open-snack-bar/open-snack-bar.service';
import { SplashScreenComponent } from './_shared/components/splash-screen/splash-screen.component';
import { SplashScreenService } from './_shared/components/splash-screen/splash-screen.service';
import { SessionStorageService, StorageService } from './common/storage/storage.service';
import { UserService } from './common/user/user.service';
import { baseUrl, getDefaultModalSettings, navigateToExternalUrl } from './common/utils/helpers';
import { MaintenanceSiteGQL } from './pages/maintenance/maintenance.query.generated';
import { SearchEnginePositionService } from './pages/search-results/search-engine-position.service';
import { BottomBarComponent } from './_shared/components/bottom-bar/bottom-bar.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, AsyncPipe, RouterOutlet, CookieButtonComponent, SplashScreenComponent, BottomBarComponent],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  currentSessionId: string;
  snackBarRef: MatSnackBarRef<any>;
  online$: Observable<boolean>;
  offline$: Observable<boolean>;
  onlineListener: () => void;
  offlineListener: () => void;
  private firstAccessReferrer = '';
  private offlineSheet: MatBottomSheetRef<any>;
  private previousUrl: string;
  isEmbedded = false;

  constructor(
    @Inject(LOCAL_STORAGE) public localStorage: Storage,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    @Inject(NAVIGATOR) private navigator: Navigator,
    private sessionStorageService: SessionStorageService,
    private localStorageService: StorageService,
    private location: Location,
    private cookiePreferenceCreateGQL: CookiePreferenceCreateGQL,
    private cookiePreferenceGQL: CookiePreferenceGQL,
    @Inject(I18NEXT_SERVICE) private translateService: ITranslationService,
    private activatedRoute: ActivatedRoute,
    private bottomSheet: MatBottomSheet,
    public router: Router,
    private locationSearchService: LocationSearchService,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private openDialogService: OpenDialogService,
    private signinService: SignInService,
    private analyticsService: AnalyticsService,
    private googleAnalyticsService: GoogleAnalyticsService,
    public userService: UserService,
    private navigationService: NavigationService,
    private openSnackBarService: OpenSnackBarService,
    private ngZone: NgZone,
    private cd: ChangeDetectorRef,
    public cookieService: CookieService,
    public splashScreenService: SplashScreenService,
    private maintenanceSiteGQL: MaintenanceSiteGQL,
    @Inject(CAPACITOR_SERVICE) private capacitorService: CapacitorService,
    private searchEnginePositionService: SearchEnginePositionService,
    private route: ActivatedRoute,
  ) {
    super();
    this.sessionStorageService.clear();
    // T0 D0, used to do something this.localStorageService.remove('cart_uuid');
    // Object.defineProperty(this.document, 'referrer', {
    //   get: () => baseUrl + (this.navigationService.getPreviousUrl() || ''),
    //   configurable: true,
    // });
  }

  @HostListener('document:click', ['$event']) onClick(event: Event) {
    const target: HTMLElement = event.target as HTMLElement;
    const url = target?.getAttribute('href');
    if (!url) return;
    if (target?.getAttribute('target') === '_blank') {
      navigateToExternalUrl(this.window, url, this.openSnackBarService, this.translateService, undefined, event);
    } else if (target?.classList.contains('internal-link')) {
      event.preventDefault();
      const urlLang: 'it' | 'en' | 'es' | 'fr' | 'de' = url.substring(0, 3).replace('/', '') as any;
      if (urlLang !== this.translateService.language) {
        const mainPath = url.substring(4).split('/')[0];
        const routeKey = Object.entries(routes[urlLang]).find(([_key, value]) => value === mainPath)?.[0];
        if (routeKey) {
          const routeTranslation = routes[this.translateService.language as 'it'][routeKey];
          const translatedUrl = `/${this.translateService.language}/${routeTranslation}/${url.replace(
            `/${urlLang}/${mainPath}/`,
            '',
          )}`;
          this.router.navigateByUrl(translatedUrl, {
            state: { refresh: true },
          });
        } else {
          this.router.navigateByUrl(url, {
            state: { refresh: true },
          });
        }
      } else {
        this.ngZone.run(() =>
          this.router.navigateByUrl(url, {
            state: { refresh: true },
          }),
        );
      }
    }
  }

  ngOnInit(): void {
    // METADATA
    this.initMetadata();
    // ROUTER e CSP
    this.subscriptions.push(
      fromEvent(this.document, 'securitypolicyviolation', { passive: true }).subscribe(() =>
        console.error.bind(console),
      ),
      this.routingEvents().subscribe(),
    );

    if (this.isPlatformBrowser()) {
      if (this.userService.user && this.userService.user.favouriteLanguage !== this.translateService.language) {
        // FAVOURITE LANGUAGE
        this.showLanguageSnackbar();
      }
      this.firstAccessReferrer = this.document?.referrer;
      // // EXTERNAL REFERRER
      // if (!(this.document?.referrer || '').startsWith(baseUrl, 0)) {
      //
      // }
      // CONNECTION
      this.checkConnectionStatus();
      // REFERRAL CODE
      const referral = this.getParameterByName('referral', this.document.location.href);
      if (referral) this.localStorage.setItem('referral-code', referral);
      // COOKIE
      const uuid = this.localStorage.getItem('cookie_preference_uuid');
      // CAPACITOR
      if (Capacitor.isNativePlatform()) {
        this.setupCapacitor();
      }
      if (environment.production) {
        if (!this.document.getElementById('gtag-script') && !!environment.gtag) {
          const gTagScript = this.document.createElement('script');
          gTagScript.id = 'gtag-script';
          gTagScript.type = 'text/javascript';
          gTagScript.innerText = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${environment.gtag}');`;
          this.document.head.insertAdjacentElement('afterbegin', gTagScript);
        }
        if (!this.document.getElementById('gtag-noscript') && !!environment.gtag) {
          const noTagScript = this.document.createElement('noscript');
          noTagScript.id = 'gtag-noscript';
          const iframe = this.document.createElement('iframe');
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${environment.gtag}`;
          iframe.height = '0';
          iframe.width = '0';
          iframe.style.display = 'none';
          iframe.style.visibility = 'hidden';
          noTagScript.appendChild(iframe);
          this.document.body.insertAdjacentElement('afterbegin', noTagScript);
        }
        this.subscriptions.push(
          (Capacitor.isNativePlatform() ? from(App.getInfo()) : of({ name: '', id: '', build: '', version: '' }))
            .pipe(
              // MAINTENANCE
              mergeMap(({ version }) => {
                const query = this.maintenanceSiteGQL.watch(
                  { version, platform: Capacitor.getPlatform() },
                  { fetchPolicy: 'no-cache' },
                );
                query.startPolling(60000);
                return query.valueChanges;
              }),
              map(r => r.data.maintenanceSite),
            )
            .subscribe({
              next: ({ deprecate, maintenance }) => {
                if (this.userService?.user?.role !== UserRole.Admin) {
                  if (deprecate) {
                    this.router.navigate(['/deprecated']);
                  }
                  if (maintenance) {
                    this.router.navigate(['/maintenance']);
                  }
                }
              },
            }),
        );
      }
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const embedParam = urlParams.get('embed');
      this.isEmbedded = embedParam === 'true';
      if (!this.isEmbedded) {
        this.initSharryLand(uuid);
      }
    }
    this.cd.detectChanges();
  }

  private initSharryLand = (uuid: string) => {
    this.subscriptions.push(
      this.offline$.subscribe({
        next: v => {
          if (v) {
            import('./_shared/dialogs/no-connection-dialog/no-connection-dialog.component').then(
              ({ NoConnectionDialogComponent }) =>
                (this.offlineSheet = this.bottomSheet.open(NoConnectionDialogComponent, {
                  backdropClass: 'sharry-no-backdrop',
                  panelClass: 'sharry-no-connection-panel',
                  scrollStrategy: new NoopScrollStrategy(),
                  disableClose: false,
                })),
            );
          }
        },
      }),
      this.online$.subscribe({
        next: v => {
          if (v && this.offlineSheet) {
            this.offlineSheet.dismiss();
            this.window.location.reload();
          }
        },
      }),
      (uuid && !this.usingCapacitor
        ? this.cookiePreferenceGQL.fetch({ uuid }).pipe(map(r => r.data.cookiePreferenceByUuid))
        : this.onShowCookieBanner()
      ).subscribe({
        next: cookiePreference => {
          if (cookiePreference) {
            const consent = this.localStorage.getItem('sharrylandConsent');
            if (!consent) {
              this.localStorage.setItem('sharrylandConsent', JSON.stringify(cookiePreference));
            }
          }
          this.signinService.cookiePreference = cookiePreference as CookiePreference;
          this.generateScriptsAndOAuth();
        },
      }),
      fromEvent(this.window, 'beforeunload', { passive: true })
        .pipe(
          mergeMap(() => {
            if (this.currentSessionId) {
              return this.analyticsService.close(this.currentSessionId);
            }
            return of(null);
          }),
        )
        .subscribe(),
      fromEvent(this.document, 'visibilitychange', { passive: true })
        .pipe(
          mergeMap(() => {
            if (this.document.visibilityState === 'hidden' && !!this.currentSessionId) {
              const id = this.currentSessionId;
              this.currentSessionId = undefined;
              return this.analyticsService.close(id);
            } else if (
              this.document.visibilityState === 'visible' &&
              !this.currentSessionId &&
              this.isPlatformBrowser()
            ) {
              return this.analyticsService.init().pipe(
                tap(id => {
                  if (id) {
                    this.currentSessionId = id;
                    this.analyticsService.updateNavigation(this.currentSessionId, this.location.path());
                  }
                }),
              );
            }
            return of(null);
          }),
        )
        .subscribe(),
      this.analyticsService.init().subscribe(id => {
        if (id) {
          this.currentSessionId = id;
          this.analyticsService.updateNavigation(this.currentSessionId, this.router.url, this.firstAccessReferrer);
        }
      }),
      this.activatedRoute.queryParams
        .pipe(
          mergeMap(params => {
            if (Object.keys(params || {})?.length) {
              if (params['signup'] && !this.userService.user) {
                return from(import('src/app/_shared/dialogs/sign-up/sign-up.component')).pipe(
                  tap(({ SignUpComponent }) =>
                    this.openDialogService.openDialog(SignUpComponent, {
                      ...getDefaultModalSettings(),
                      maxHeight: '84vh',
                      height: 'unset',
                      id: 'sign-up',
                    }),
                  ),
                );
              } else if (params['signin'] && !this.userService.user) {
                return from(import('./_shared/dialogs/sign-in/sign-in.component')).pipe(
                  tap(({ SignInComponent }) =>
                    this.openDialogService.openDialog(SignInComponent, {
                      ...getDefaultModalSettings(),
                      maxHeight: '84vh',
                      height: 'unset',
                      id: 'sign-in',
                    }),
                  ),
                );
              } else if (params['signout'] && !!this.userService.user) {
                return from(import('src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component')).pipe(
                  mergeMap(({ ConfirmDialogComponent }) =>
                    this.openDialogService
                      .openDialog(ConfirmDialogComponent, {
                        ...getDefaultModalSettings(),
                        maxHeight: '84vh',
                        height: 'unset',
                        id: 'sign-out',
                        data: {
                          title: this.translateService.t('profile-home.exit-dialog.title'),
                          text: this.translateService.t('profile-home.exit-dialog.text'),
                        },
                      })
                      .afterClosed(),
                  ),
                  tap(confirm => {
                    if (confirm) {
                      this.userService.signOut();
                    } else {
                      this.router.navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: {},
                        replaceUrl: true,
                      });
                    }
                  }),
                );
              } else if (params['forgotpassword'] && !this.userService.user) {
                return from(import('./_shared/dialogs/forgot-password/forgot-password.component')).pipe(
                  tap(({ ForgotPasswordComponent }) =>
                    this.openDialogService.openDialog(ForgotPasswordComponent, {
                      ...getDefaultModalSettings(),
                    }),
                  ),
                );
              } else if (
                params['profileemailconfirm'] &&
                !!this.userService.user &&
                !this.userService.user.confirmedAt
              ) {
                return from(import('./_shared/dialogs/profile-email-confirm/profile-email-confirm.component')).pipe(
                  mergeMap(({ ProfileEmailConfirmComponent }) =>
                    this.openDialogService
                      .openDialog(ProfileEmailConfirmComponent, {
                        ...getDefaultModalSettings(),
                        disableClose: true,
                        id: 'profileemailconfirm',
                      })
                      .afterClosed(),
                  ),
                  mergeMap(confirmed => {
                    if (confirmed) {
                      return this.userService.fetchUserAndPolicy().pipe(map(() => confirmed));
                    }
                    return of(confirmed);
                  }),
                  mergeMap(confirmed => {
                    if (confirmed) {
                      return from(import('./_shared/dialogs/onboarding/onboarding.component'));
                    }
                    return NEVER;
                  }),
                  tap(({ OnboardingComponent }) => {
                    this.openDialogService.openDialog(OnboardingComponent, {
                      ...{
                        autoFocus: false,
                        restoreFocus: false,
                        width: '90vw',
                        maxWidth: '640px',
                        minHeight: '640px',
                        maxHeight: '84vh',
                      },
                    });
                  }),
                );
              }
              return NEVER;
            }
            return NEVER;
          }),
        )
        .subscribe(),
      this.locationSearchService.loading
        .asObservable()
        .pipe(
          mergeMap(isLoading => {
            if (isLoading)
              return from(import('./_shared/components/generic-snack-bar/generic-snack-bar.component')).pipe(
                map(({ GenericSnackBarComponent }) => {
                  this.snackBarRef = this.openSnackBarService.openSnackBar(GenericSnackBarComponent, {
                    data: { text: 'snack-bar.search.geolocation.in-progress' },
                  });
                  return isLoading;
                }),
              );
            return of(isLoading);
          }),
        )
        .subscribe({
          next: isLoading => {
            if (!isLoading) {
              if (this.snackBarRef) this.snackBarRef.dismiss();
            }
          },
        }),
    );
  };

  initMetadata() {
    this.document.querySelector('html').setAttribute('lang', this.translateService.language);
    // set prerender tags
    this.metadataService.setTitle();
    this.metadataService.setDescription();
    this.metadataService.setTags([
      { property: 'og:image', content: `https://media.sharry.land/app/fb-image.webp` },
      { property: 'og:image:url', content: `https://media.sharry.land/app/fb-image.webp` },
    ]);
  }

  setAlternate(url: string) {
    const translateRoute =
      Object.entries(routes[this.translateService.language as 'it']).find(
        ([_key, value]) =>
          url.replace('/it/', '').replace('/en/', '').replace('/es/', '').replace('/fr/', '').replace('/de/', '') ===
          value,
      ) ||
      Object.entries(routes[this.translateService.language as 'it']).find(([_key, value]) =>
        url
          .replace('/it/', '')
          .replace('/en/', '')
          .replace('/es/', '')
          .replace('/fr/', '')
          .replace('/de/', '')
          .startsWith(value),
      );
    if (translateRoute) {
      const path = url.replace(`/${this.translateService.language}/${translateRoute[1]}`, '');
      ['it', 'en', 'es', 'fr', 'de'].forEach(lang => {
        const value = routes[lang as 'it'][translateRoute[0]];
        this.metadataService.setAlternate(
          lang as 'it' | 'en' | 'es' | 'fr' | 'de',
          `${baseUrl}/${lang}/${value}${path}`,
        );
      });
      this.metadataService.setAlternate('x-default', `${baseUrl}/en/${routes['en'][translateRoute[0]]}${path}`);
    } else {
      ['it', 'en', 'es', 'fr', 'de'].forEach(lang => {
        this.metadataService.setAlternate(lang as 'it' | 'en' | 'es' | 'fr' | 'de', `${baseUrl}/${lang}/`);
      });
      this.metadataService.setAlternate('x-default', `${baseUrl}/en/`);
    }
  }

  routingEvents() {
    let trigger: 'imperative' | 'popstate' | 'hashchange';
    return this.router.events.pipe(
      filter(r => r instanceof NavigationEnd || r instanceof NavigationStart),
      tap(r => {
        if (r instanceof NavigationStart) {
          if (r.url.startsWith('/https')) {
            console.log('asdfasdf');
            this.router.navigateByUrl(this.navigationService.getPreviousUrl());
          }
          trigger = r.navigationTrigger;
        }
        if (r instanceof NavigationEnd) {
          console.log('asdfasssss');
          const rurl = (r?.urlAfterRedirects || '/').split('?')[0];
          if (trigger === 'imperative' && this.router.lastSuccessfulNavigation?.extras?.state?.trigger !== 'popstate') {
            if (rurl.endsWith('/list')) {
              this.searchEnginePositionService.setPosition({ left: 0, top: 0 });
            }
            this.window.scrollTo({ top: 0, left: 0 });

            if (Capacitor.getPlatform() !== 'web') {
              this.document.querySelector('.app-wrapper')?.scrollTo({ top: 0, left: 0 });
            }
          }
          if (
            !this.router.lastSuccessfulNavigation?.extras?.skipLocationChange &&
            !this.router.lastSuccessfulNavigation?.extras?.replaceUrl &&
            this.router.lastSuccessfulNavigation?.extras?.state?.trigger !== 'popstate'
          ) {
            this.navigationService.setPreviousUrl(this.previousUrl || '/');
          }
          const url = (r?.url || '/').split('?')[0];
          if (this.previousUrl?.split('?')[0] !== url) {
            this.setAlternate(url);
            this.metadataService.setTags([{ property: 'og:url', content: baseUrl + url }]);
            if (this.isPlatformBrowser()) {
              this.googleAnalyticsService.logPageView(r.url);
              this.analyticsService.updateNavigation(this.currentSessionId, r.url);
            }
          }
          this.previousUrl = r?.url;
        }
      }),
    );
  }

  showLanguageSnackbar() {
    import('src/app/_shared/components/language-snack-bar/language-snack-bar.component').then(
      ({ LanguageSnackBarComponent }) =>
        this.openSnackBarService.openSnackBar(LanguageSnackBarComponent, {
          data: {
            text: 'translate-snackbar.text',
            favouriteLanguage: this.userService.user.favouriteLanguage,
          },
          panelClass: this.isPlatformBrowser() && !this.usingCapacitor ? ['language-snack-bar__position'] : [],
        }),
    );
  }

  checkConnectionStatus() {
    const networkStatus$ = new Observable<Partial<ConnectionStatus>>(s => {
      if (Capacitor.isNativePlatform()) {
        Network.getStatus()
          .then(status => s.next(status))
          .catch(() => s.next({ connected: false }));
        Network.addListener('networkStatusChange', status => {
          s.next(status);
        });
      } else {
        s.next({ connected: this.navigator.onLine });
        this.onlineListener = () => {
          s.next({ connected: true });
        };
        this.offlineListener = () => {
          s.next({ connected: false });
        };
        this.window.addEventListener('online', this.onlineListener);
        this.window.addEventListener('offline', this.offlineListener);
      }
    });

    this.online$ = networkStatus$.pipe(map(c => c.connected));
    this.offline$ = networkStatus$.pipe(map(c => !c.connected));
  }

  setupCapacitor() {
    this.document.body?.classList.add('using-capacitor');
    this.capacitorService.init();
  }

  ngAfterViewInit(): void {
    this.splashScreenService.showSplash('destroyed');
    // this.handleAppComponentsVisibility(this.location.path());
  }

  generateScriptsAndOAuth() {
    this.ngZone.runOutsideAngular(() => {
      if (!!this.userService.user && !this.usingCapacitor && !this.document.getElementById('apple-itunes-app')) {
        const meta = this.document.createElement('meta');
        meta.id = 'apple-itunes-app';
        meta.name = 'apple-itunes-app';
        meta.content = 'app-id=1477462434';
        this.document.head.insertAdjacentElement('beforeend', meta);
      }
      this.cd.detectChanges();
    });
  }

  getParameterByName(name: string, url: string) {
    name = name.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  }

  onShowCookieBanner(): Observable<Partial<CookiePreference>> {
    if (!Capacitor.isNativePlatform()) {
      return from(import('src/app/_shared/dialogs/cookie-policy-dialog/cookie-policy-dialog.component')).pipe(
        mergeMap(({ CookiePolicyDialogComponent }) =>
          this.openDialogService
            .openDialog(
              CookiePolicyDialogComponent,
              {
                ...getDefaultModalSettings(),
                panelClass: ['cookie-policy-dialog'],
              },
              false,
            )
            .afterClosed(),
        ),
        mergeMap(res => {
          console.log(res);
          switch (res) {
            case 'accept':
              this.localStorage.setItem('cookie_preference_uuid', uuidv4());
              this.cookieService.showCookieButton = true;
              this.cd.detectChanges();
              return this.cookiePreferenceCreateGQL
                .mutate(
                  {
                    input: {
                      necessary: true,
                      functional: true,
                      statistic: true,
                      advertising: true,
                      uuid: this.localStorage.getItem('cookie_preference_uuid'),
                    },
                  },
                  { fetchPolicy: 'no-cache' },
                )
                .pipe(map(r => r.data.cookiePreferenceCreate));
            case 'manage':
              this.cookieService.showCookieButton = false;
              this.cd.detectChanges();
              return this.cookieService.onManageCookies().pipe(
                finalize(() => {
                  this.cookieService.showCookieButton = true;
                  document.body.style.overflow = 'auto';
                  this.cd.detectChanges();
                }),
                switchMap(() => of(null)),
              );
            default:
              return of(null);
          }
        }),
      );
    } else {
      const cookiePref: Partial<CookiePreference> = {
        statistic: true,
        functional: true,
        advertising: true,
        necessary: true,
      };
      return of(cookiePref);
    }
  }

  ngOnDestroy(): void {
    this.sessionStorageService.clear();
    if (this.onlineListener) {
      this.window.removeEventListener('online', this.onlineListener);
    }
    if (this.offlineListener) {
      this.window.removeEventListener('offline', this.offlineListener);
    }
  }
}
